import React, { useMemo, useState } from "react";

import { PageHeader } from "@core/layouts/PageHeader";

import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { Switch } from "@shared/ui/switch";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";

import {
  Plus,
  ListChecks,
  CheckCircle2,
  Archive,
  Link2,
  Gauge,
  Box,
  Pencil,
} from "lucide-react";

import { GenericTable } from "@shared/components/GenericTable";

import { TableColumn } from "@shared/types/table";

import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";

import ParameterFormModal from "./Create";
import { ParameterFilter } from "./filter";
import { useGetParameters, useParameterSummary, useUpdateParameter } from "@features/onboarding/utility-setup/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { ParameterResponse } from "@features/onboarding/utility-setup/api";
import { PAGE_SIZE } from "@shared/utils/constants";
import { HoverRevealText } from "@shared/components/HoverRevealText";

// ─────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────

export interface ParameterRecord {
  id: string;
  name: string;
  entity: string;
  dataType: string;
  allowedValues: string[];
  appliesTo: string[];
  usedByCount: number;
  updatedAt: string;
  status: "Active" | "Inactive";
  description?: string;
  unit?: string;
  source?: "manual" | "external";
  sourceModule?: string;
}

// ─────────────────────────────────────────────────────────────────────
// Mock Data Removed
// ─────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────
// Allowed Values Cell
// ─────────────────────────────────────────────────────────────────────

const AllowedValuesCell: React.FC<{
  values: string[];
}> = ({ values }) => {
  if (!values?.length) {
    return (
      <span className="text-xs italic text-muted-foreground">
        NA
      </span>
    );
  }

  const visible = values.slice(0, 3);

  const remaining = values.length - 3;

  return (
    <div className="flex flex-wrap gap-1 items-center">
      {visible.map((value) => (
        <Badge
          key={value}
          variant="secondary"
          className="text-[10px] px-1.5 py-0"
        >
          {value}
        </Badge>
      ))}

      {remaining > 0 && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-pointer inline-flex">
                <Badge
                  variant="outline"
                  className="text-[10px] px-1.5 py-0"
                >
                  +{remaining} more
                </Badge>
              </span>
            </TooltipTrigger>

            <TooltipContent className="max-w-xs bg-white border shadow-lg p-3 z-50">
              <div className="flex flex-wrap gap-1">
                {values.map((value) => (
                  <Badge
                    key={value}
                    variant="secondary"
                    className="text-[10px] px-1.5 py-0"
                  >
                    {value}
                  </Badge>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Applies To Cell
// ─────────────────────────────────────────────────────────────────────

const AppliesToCell: React.FC<{
  values: string[];
}> = ({ values }) => {
  if (!values?.length) {
    return (
      <span className="text-xs italic text-muted-foreground">
        NA
      </span>
    );
  }

  const visible = values.slice(0, 3);
  const remaining = values.length - 3;

  return (
    <div className="flex flex-wrap gap-1 items-center">
      {visible.map((value) => (
        <Badge
          key={value}
          variant="info"
          className="text-[10px]"
        >
          {value}
        </Badge>
      ))}

      {remaining > 0 && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-pointer inline-flex">
                <Badge
                  variant="outline"
                  className="text-[10px] px-1.5 py-0"
                >
                  +{remaining} more
                </Badge>
              </span>
            </TooltipTrigger>

            <TooltipContent className="max-w-xs bg-white border shadow-lg p-3 z-50">
              <div className="flex flex-wrap gap-1">
                {values.map((value) => (
                  <Badge
                    key={value}
                    variant="info"
                    className="text-[10px]"
                  >
                    {value}
                  </Badge>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

const ParameterRegistryList: React.FC = () => {
  // const [parameters, setParameters] =
  //   useState<ParameterRecord[]>(MOCK_PARAMETERS);

  const [modalOpen, setModalOpen] =
    useState(false);

  const [editingParameter, setEditingParameter] =
    useState<ParameterRecord | null>(null);

  const [filters, setFilters] = useState({
    entity: "",
    status: "",
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const limit = PAGE_SIZE;

  const { data: apiResponse } = useGetParameters(
    filters.entity || undefined,
    filters.status || undefined,
    searchQuery || undefined,
    page,
    limit
  );

  const {data:summary}=useParameterSummary();
  const updateMutation = useUpdateParameter();
  const queryClient = useQueryClient();

  const parameters = apiResponse?.result || apiResponse?.results || [];


  // ───────────────────────────────────────────────────────────────────
  // Stats Cards
  // ───────────────────────────────────────────────────────────────────

  const statsCards = [
    {
      title: "Total",
      count: summary?.total || 0,
      trendSummary: "Total parameters defined",
      icon: (
        <ListChecks className="h-5 w-5 text-blue-600" />
      ),
    },

    {
      title: "Active",
      count: summary?.active || 0,
      trendSummary: "Usable in rules",
      icon: (
        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
      ),
    },

    {
      title: "Inactive",
      count: summary?.inactive || 0,
      trendSummary: "Archived parameters",
      icon: (
        <Archive className="h-5 w-5 text-gray-500" />
      ),
    },
  ];

  // ───────────────────────────────────────────────────────────────────
  // Current Filters
  // ───────────────────────────────────────────────────────────────────

  const currentFilters = useMemo(
    () => ({
      entity: filters.entity,
      status: filters.status,
    }),
    [filters]
  );

  // ───────────────────────────────────────────────────────────────────
  // Modal Handlers
  // ───────────────────────────────────────────────────────────────────

  const handleToggleStatus = (row: ParameterResponse, newValue: boolean) => {
    updateMutation.mutate({
      id: String(row.id),
      payload: {
        is_active: newValue,
      }
    }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["onboarding", "parameter"] });
      }
    });
  };

  const handleOpenCreate = () => {
    setEditingParameter(null);

    setModalOpen(true);
  };

  const handleEdit = (
    row: ParameterResponse
  ) => {
    setEditingParameter({
      id: String(row.id),
      name: row.name,
      entity: String(row.entityType),
      dataType: row.dataTypeDisplay,
      allowedValues: row.allowedValues || [],
      appliesTo: row.appliesToServices || [],
      usedByCount: row.usageCount || 0,
      updatedAt: row.lastModifiedDate || row.createdDate,
      status: row.statusDisplay === "Active" ? "Active" : "Inactive",
      description: row.description,
      unit: row.unit || undefined,
      source: row.valuesSourceDisplay?.toLowerCase() as any,
      sourceModule: row.valuesSourceId ? String(row.valuesSourceId) : undefined,
    });

    setModalOpen(true);
  };

  const handleSave = () => {
    setModalOpen(false);
    setEditingParameter(null);
  };

  // ───────────────────────────────────────────────────────────────────
  // Filtered Data
  // ───────────────────────────────────────────────────────────────────

  // (Server-side filtering is active)

  // ───────────────────────────────────────────────────────────────────
  // Columns
  // ───────────────────────────────────────────────────────────────────

  const columns: TableColumn<ParameterResponse>[] =
    useMemo(
      () => [
        {
          key: "name",
          header: "Parameter",
          sortable: true,

          render: (
            value: string,
            row: ParameterResponse
          ) => (
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {value}
              </p>

              {row.description && (
                <HoverRevealText text={row.description} />
              )}
            </div>
          ),
        },

        {
          key: "entityTypeDisplay",
          header: "Entity",
          sortable: true,

          render: (value: string) => (
            <Badge variant="outline">
              {value}
            </Badge>
          ),
        },
        {
          key: "allowedValuesDisplay",
          header: "Allowed Values",

          render: (
            _: any,
            row: ParameterResponse
          ) => (
            <AllowedValuesCell
              values={row.allowedValues}
            />
          ),
        },

        {
          key: "appliesToServices",
          header: "Applies To",

          render: (value: string[]) => (
            <AppliesToCell values={value} />
          ),
        },

        {
          key: "usageCount",
          header: "Used By",
          sortable: true,

          render: (value: number) => (
            <span className="text-xs font-medium">
              {value} rules
            </span>
          ),
        },

        {
          key: "lastModifiedDate",
          header: "Updated",
        },

        {
          key: "isActive",
          header: "Status",
          sortable: true,

          render: (value: boolean, row: ParameterResponse) => (
            <div className="flex items-center gap-2">
              <Badge
                variant={
                  value
                    ? "success"
                    : "secondary"
                }
              >
                {value ? "Active" : "Inactive"}
              </Badge>
            </div>
          ),
        },
        {
          key: "actions",
          header: "Actions",
          render: (_: any, row: ParameterResponse) => (
            <div className="flex items-center gap-4">
              <Switch
                checked={row.isActive}
                onCheckedChange={(checked) => handleToggleStatus(row, checked)}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(row)}
                className="h-8 w-8 p-0"
              >
                <Pencil className="h-4 w-4 text-gray-500" />
              </Button>
            </div>
          ),
        },
      ],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

  // ───────────────────────────────────────────────────────────────────
  // Filter Handlers
  // ───────────────────────────────────────────────────────────────────

  const handleApplyFilters = (
    newFilters: any
  ) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      entity: "",
      status: "",
    });
    setPage(1);
  };

  // ───────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}

      <PageHeader
        title="Parameter Registry"
        description="Catalog of entity attributes that conditional charge rules can reference."
      >
        <Button
          size="sm"
          onClick={handleOpenCreate}
        >
          <Plus className="mr-2 h-4 w-4" />
          New Parameter
        </Button>
      </PageHeader>

      {/* KPI Cards */}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
        {statsCards.map((card) => (
          <QuickStatsCard
            key={card.title}
            title={card.title}
            count={card.count}
            trendSummary={card.trendSummary}
            icon={card.icon}
          />
        ))}
      </div>

      {/* Table */}

      <Card>
        <CardContent className="pt-2">
          <GenericTable<ParameterResponse>
            data={parameters}
            columns={columns}
            rowKey="id"
            searchPlaceholder="Search parameters..."
            onSearch={(value) => setSearchQuery(value)}
            emptyMessage="No parameters found."
            pagination={{
              currentPage: apiResponse?.page || page,
              totalPages: Math.ceil((apiResponse?.count || 0) / limit) || 1,
              totalItems: apiResponse?.count || 0,
              startItem: apiResponse?.count ? ((apiResponse?.page || page) - 1) * limit + 1 : 0,
              endItem: Math.min((apiResponse?.page || page) * limit, apiResponse?.count || 0),
            }}
            onPageChange={(newPage: number) => setPage(newPage)}
            filterPanel={
              <ParameterFilter
                currentFilters={
                  currentFilters
                }
                onApplyFilters={
                  handleApplyFilters
                }
                onResetFilters={
                  handleResetFilters
                }
              />
            }
          />
        </CardContent>
      </Card>

      {/* Modal */}

      <ParameterFormModal
        open={modalOpen}
        initialData={editingParameter}
        onSave={handleSave}
        onOpenChange={(open) => {
          setModalOpen(open);

          if (!open) {
            setEditingParameter(null);
          }
        }}
      />
    </div>
  );
};

export default ParameterRegistryList;
